import { createPortal } from "react-dom";
import { LoaderCircle, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import {
  apiClient,
  type CorrectionGroup,
  type ImportMappingInput
} from "@/shared/api/api-client";
import { errorMessage } from "@/shared/api/http";
import { usePolarityChoices } from "@/shared/components/polarity-choices";
import { SearchSelect, type SearchSelectOption } from "@/shared/components/SearchSelect";
import { useDismiss } from "@/shared/hooks/useDismiss";
import { APPROACH_CHOICES } from "@/features/mentions/mention-filter-criteria";
import { importGroupTitle, importValueName } from "./import-group";

/**
 * "Usar um existente" (2026-10-08): para um grupo da fila cujo valor a
 * planilha trouxe e o cadastro não tem, a pessoa escolhe o item do
 * Reputaition que esse valor significa.
 *
 * **Vale só pra este upload.** Não há tabela de sinônimos que a empresa
 * herde (decisão da dona: "melhor a pessoa subir o padrão, pode dar muita
 * confusão") — a próxima planilha com `pos` cai na fila de novo. O texto
 * da tela diz isso, porque a expectativa natural é o contrário.
 *
 * O botão faz os dois passos que o servidor separa — guarda a escolha e
 * manda reprocessar o grupo —, porque guardar sem reprocessar deixa o
 * grupo parado e a pessoa sem saber por quê.
 */

type Props = {
  token: string;
  uploadId: number;
  group: CorrectionGroup;
  brandName?: string | null;
  /** Fonte do template do upload; filtra as mensagens-chave, que são
   *  catálogo por fonte. */
  postSource?: "press" | "network" | null;
  onClose: () => void;
  /** Depois de guardar E reprocessar. */
  onDone: () => void;
  /** Quem reprocessa — vem da página, que já sabe atualizar a fila. */
  reprocess: (group: CorrectionGroup) => Promise<void>;
};

export function UseExistingDialog({
  token,
  uploadId,
  group,
  brandName,
  postSource,
  onClose,
  onDone,
  reprocess
}: Props) {
  useDismiss(true, onClose);
  const polarityChoices = usePolarityChoices();
  const dimension = group.import_dimension;
  const [options, setOptions] = useState<SearchSelectOption[] | null>(null);
  const [chosen, setChosen] = useState("");
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const brandId = group.import_brand_id;
  // Polaridade e abordagem são vocabulário que a tela já tem: viram
  // opções no RENDER, não num efeito. Até 2026-10-09 as quatro dimensões
  // passavam pelo mesmo efeito, com `polarityChoices` nas dependências —
  // e como `usePolarityChoices()` devolvia um array novo a cada render,
  // cada `setOptions` re-disparava o efeito: tema e mensagem-chave
  // pediam o catálogo em LAÇO até a cerca de 429 (a classe do laço do
  // detalhe do upload, 2026-09-14), e polaridade girava em laço de
  // render. O hook ganhou `useMemo`, mas o diálogo não depende disso:
  // o efeito abaixo só conhece as duas dimensões que vão ao servidor.
  const staticOptions = useMemo<SearchSelectOption[] | null>(() => {
    if (dimension === "polarity") {
      return polarityChoices.map(p => ({ value: p.name, label: p.label }));
    }
    if (dimension === "approach") {
      return APPROACH_CHOICES.map(a => ({ value: a.value, label: a.label }));
    }
    return null;
  }, [dimension, polarityChoices]);
  useEffect(() => {
    if (dimension === "polarity" || dimension === "approach") return;
    let cancelled = false;
    const done = (items: SearchSelectOption[]) => {
      if (!cancelled) setOptions(items);
    };
    if (dimension === "themes" && brandId !== null) {
      apiClient
        .listThemes(token, { brandId })
        .then(themes =>
          done(
            themes
              .filter(theme => theme.is_active)
              .map(theme => ({ value: String(theme.id), label: theme.name }))
          )
        )
        .catch(error => {
          if (!cancelled) setFailure(errorMessage(error, "Não foi possível carregar os subtemas."));
        });
    } else if (dimension === "key_messages" && brandId !== null) {
      apiClient
        .listKeyMessages(token, { brandId })
        .then(messages =>
          done(
            messages
              .filter(m => m.is_active && (!postSource || m.data_source === postSource))
              .map(m => ({ value: String(m.id), label: m.message }))
          )
        )
        .catch(error => {
          if (!cancelled) setFailure(errorMessage(error, "Não foi possível carregar as mensagens."));
        });
    } else {
      done([]);
    }
    return () => {
      cancelled = true;
    };
  }, [token, dimension, brandId, postSource]);
  const shownOptions = staticOptions ?? options;

  const body = useMemo<ImportMappingInput | null>(() => {
    if (!dimension || !chosen || !group.import_value) return null;
    const base = { upload_id: uploadId, dimension, value: group.import_value };
    if (dimension === "polarity" || dimension === "approach") {
      return { ...base, target_value: chosen };
    }
    return { ...base, brand_id: brandId, target_id: Number(chosen) };
  }, [dimension, chosen, group.import_value, uploadId, brandId]);

  const submit = async () => {
    if (!body) return;
    setSaving(true);
    setFailure(null);
    try {
      await apiClient.saveImportMapping(token, body);
      await reprocess(group);
      onDone();
    } catch (error) {
      setFailure(errorMessage(error, "Não foi possível guardar a escolha."));
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="modal-overlay" onClick={saving ? undefined : onClose}>
      <div
        className="modal-container use-existing-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="use-existing-title"
        onClick={event => event.stopPropagation()}
      >
        <header className="modal-header">
          <div>
            <span className="eyebrow">USAR UM EXISTENTE</span>
            <h2 id="use-existing-title">{importGroupTitle(group, brandName)}</h2>
          </div>
          <button
            className="icon-button"
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            disabled={saving}
          >
            <X size={18} />
          </button>
        </header>
        <div className="modal-body">
          <p className="use-existing-lead">
            Escolha o que “{importValueName(group)}” significa no Reputaition. A escolha vale
            só para <strong>este upload</strong> — uma planilha nova com o mesmo texto volta
            para a fila.
          </p>
          {shownOptions === null && !failure ? (
            <p className="use-existing-loading">
              <LoaderCircle className="spin" size={15} /> Carregando…
            </p>
          ) : (
            <SearchSelect
              label="Item do cadastro"
              value={chosen}
              valueLabel={shownOptions?.find(o => o.value === chosen)?.label}
              options={shownOptions ?? []}
              onChange={setChosen}
              allLabel="Escolher…"
              required
            />
          )}
          {failure && (
            <p className="use-existing-error" role="alert">
              {failure}
            </p>
          )}
          <div className="use-existing-actions">
            <button type="button" className="text-button" onClick={onClose} disabled={saving}>
              Cancelar
            </button>
            <button
              type="button"
              className="primary-button"
              disabled={!body || saving}
              onClick={() => void submit()}
            >
              {saving && <LoaderCircle className="spin" size={15} />}
              Usar e reprocessar {group.pending === 1 ? "a linha" : `as ${group.pending} linhas`}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
